/**
 * A mesma negociação, por texto — o canal WhatsApp da demonstração.
 *
 * O cérebro é o MESMO do Modelo B: `POST /api/pipeline/calls/{id}/respond`,
 * com o prompt compilado da política publicada e as ferramentas sob a mesma
 * governança. Aqui só muda o transporte: o apresentador digita como a cliente
 * e a Alice responde em mensagens. O console de supervisão espelha a conversa
 * como espelha uma chamada de voz — mesmos eventos, mesmo reducer.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import QRCode from 'qrcode'

import { api } from '../lib/api'
import { clock } from '../lib/format'
import { openStream, type StreamHandle, type StreamMessage } from '../lib/stream'
import { radius, shadow } from '../theme/tokens'
import type { Artifact, Case, PendingHandoff, Scenario } from '../lib/types'

// Paleta do WhatsApp escuro — proposital e local: esta tela imita o canal,
// não o design system do hub.
const WA = {
  header: '#202c33',
  bg: '#0b141a',
  received: '#202c33',
  sent: '#005c4b',
  text: '#e9edef',
  faint: '#8696a0',
  accent: '#00a884',
  input: '#2a3942',
}

type Phase = 'setup' | 'live' | 'ended'

interface Bubble {
  id: number
  from: 'ai' | 'cli'
  text?: string
  artifact?: Artifact
  at: string
}

export function WhatsApp() {
  const [phase, setPhase] = useState<Phase>('setup')
  const [cases, setCases] = useState<Case[]>([])
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [scenarioId, setScenarioId] = useState('2')
  const [agentName, setAgentName] = useState('Alice')
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const [typing, setTyping] = useState(false)
  const [draft, setDraft] = useState('')
  const [supervisors, setSupervisors] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [handoff, setHandoff] = useState<PendingHandoff | null>(null)

  const stream = useRef<StreamHandle | null>(null)
  const callId = useRef<string | null>(null)
  // ?continuar=1: a tela veio do botão da chamada — retoma a conversa sozinha
  // assim que o hand-off e os dados carregarem, sem passar pelo setup.
  const autoAttach = useRef(new URLSearchParams(window.location.search).has('continuar'))
  const seq = useRef(0)
  const elapsedRef = useRef(0)
  const phaseRef = useRef<Phase>('setup')
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  phaseRef.current = phase

  const at = useCallback(() => clock(elapsedRef.current), [])

  const publish = useCallback(
    (message: Parameters<StreamHandle['send']>[0]) => stream.current?.send(message),
    [],
  )

  const scenario = useMemo(
    () => scenarios.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  )
  const debtor = useMemo(
    () => cases.find((c) => c.id === scenario?.case_id) ?? null,
    [cases, scenario],
  )

  useEffect(() => {
    Promise.all([api.listCases(), api.listScenarios(), api.pipelineStatus(), api.getPublished()])
      .then(([caseList, scenarioList, status, policy]) => {
        setCases(caseList)
        setScenarios(scenarioList)
        setEnabled(status.enabled)
        setAgentName(policy.persona.name)
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Falha ao carregar os dados'),
      )
  }, [])

  const hangup = useCallback(
    (notify = true) => {
      if (notify) publish({ type: 'ended', at: at() })
      setPhase('ended')
      setTyping(false)
    },
    [publish, at],
  )

  const runTurn = useCallback(
    async (text: string, role: 'user' | 'system') => {
      const id = callId.current
      if (!id) return
      setTyping(true)
      try {
        const turn = await api.pipelineRespond(id, text, role, at())

        for (const tool of turn.executed_tools) {
          if (!tool.ok || !tool.artifact) continue
          const artifact = { ...tool.artifact }
          if (artifact.kind === 'pix' && artifact.code) {
            try {
              artifact.qr_svg = await QRCode.toString(artifact.code, {
                type: 'svg',
                margin: 0,
                errorCorrectionLevel: 'M',
              })
            } catch {
              artifact.qr_svg = null
            }
          }
          publish({ type: 'artifact', artifact, timeline: tool.timeline })
          seq.current += 1
          setBubbles((current) => [
            ...current,
            { id: seq.current, from: 'ai', artifact, at: at() },
          ])
        }

        if (turn.text) {
          publish({ type: 'transcript', speaker: 'ai', delta: turn.text, at: at() })
          publish({ type: 'turn_done', speaker: 'ai' })
          seq.current += 1
          setBubbles((current) => [
            ...current,
            { id: seq.current, from: 'ai', text: turn.text, at: at() },
          ])
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao responder')
      } finally {
        setTyping(false)
      }
    },
    [publish, at],
  )

  // ------------------------------------------------- comandos do supervisor --
  const onMessage = useCallback(
    (message: StreamMessage) => {
      switch (message.type) {
        case 'hello':
        case 'peers':
          setSupervisors(message.peers.supervisor ?? 0)
          break
        case 'command':
          if (message.command === 'whisper') {
            void runTurn(`Orientação do supervisor: ${message.text}`, 'system')
          }
          if (message.command === 'hangup' && phaseRef.current === 'live') hangup(false)
          break
        default:
          break
      }
    },
    [runTurn, hangup],
  )

  useEffect(() => {
    stream.current = openStream('line', { onMessage })
    return () => {
      stream.current?.close()
      stream.current = null
    }
  }, [onMessage])

  // Hand-off voz → WhatsApp: enquanto a tela espera, sonda se alguma ligação
  // foi movida para cá pela ferramenta continuar_whatsapp.
  useEffect(() => {
    if (phase !== 'setup') return
    let alive = true
    const poll = () =>
      api
        .pipelineHandoff()
        .then((data) => {
          if (alive) setHandoff(data.handoff)
        })
        .catch(() => undefined)
    poll()
    const id = window.setInterval(poll, 3000)
    return () => {
      alive = false
      window.clearInterval(id)
    }
  }, [phase])

  // Relógio espelhado para o console, como na chamada de voz.
  useEffect(() => {
    if (phase !== 'live') return
    const id = window.setInterval(() => {
      elapsedRef.current += 1
      publish({ type: 'tick', elapsed: elapsedRef.current })
    }, 1000)
    return () => window.clearInterval(id)
  }, [phase, publish])

  // Conversa gruda no fim, como no aplicativo real.
  useEffect(() => {
    const node = scrollRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [bubbles, typing])

  // O apresentador não deve precisar clicar de novo a cada resposta: quando a
  // Alice termina de "digitar", o foco volta para o campo de mensagem.
  useEffect(() => {
    if (phase === 'live' && !typing) inputRef.current?.focus()
  }, [phase, typing])

  const start = useCallback(async () => {
    if (!debtor || !scenario) return
    setError(null)
    setBubbles([])
    elapsedRef.current = 0

    try {
      const call = await api.createCall(debtor.id, scenario.id, 'wa')
      callId.current = call.id
      publish({
        type: 'dial',
        call_id: call.id,
        case_id: debtor.id,
        scenario_id: scenario.id,
        case_name: debtor.name,
        case_phone: debtor.phone,
        agent_name: agentName,
      })
      publish({ type: 'answered', at: '00:00' })
      setPhase('live')
      // Cobrança ativa: a Alice manda a primeira mensagem.
      await runTurn(
        'Inicie a conversa agora: envie a primeira mensagem de abertura da '
          + 'cobrança para a cliente, conforme a política.',
        'system',
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar a conversa')
      setPhase('setup')
    }
  }, [debtor, scenario, agentName, publish, runTurn])

  // Continua a ligação movida para cá: mesmo call_id, mesmo histórico — a
  // Alice retoma de onde a voz parou, agora em texto.
  const attach = useCallback(async () => {
    if (!handoff) return
    const attachedCase = cases.find((c) => c.id === handoff.case_id) ?? null
    setError(null)
    setBubbles([])
    elapsedRef.current = 0
    callId.current = handoff.call_id
    publish({
      type: 'dial',
      call_id: handoff.call_id,
      case_id: handoff.case_id,
      scenario_id: handoff.scenario_id,
      case_name: attachedCase?.name ?? '—',
      case_phone: attachedCase?.phone ?? '',
      agent_name: agentName,
    })
    publish({ type: 'answered', at: '00:00' })
    setPhase('live')
    await runTurn(
      'A cliente pediu para continuar a conversa pelo WhatsApp. A partir de '
        + 'agora o canal é texto: mensagens curtas, sem markdown, valores em '
        + 'dígitos. Envie agora a primeira mensagem retomando a negociação '
        + 'exatamente de onde parou.',
      'system',
    )
  }, [handoff, cases, agentName, publish, runTurn])

  useEffect(() => {
    if (!autoAttach.current) return
    if (phase !== 'setup' || !handoff || !enabled || cases.length === 0) return
    autoAttach.current = false
    void attach()
  }, [phase, handoff, enabled, cases, attach])

  const send = useCallback(() => {
    const text = draft.trim()
    if (!text || typing || phase !== 'live') return
    setDraft('')
    publish({ type: 'transcript', speaker: 'cli', delta: text, at: at() })
    publish({ type: 'turn_done', speaker: 'cli' })
    seq.current += 1
    setBubbles((current) => [...current, { id: seq.current, from: 'cli', text, at: at() }])
    void runTurn(text, 'user')
  }, [draft, typing, phase, publish, at, runTurn])

  const restart = useCallback(() => {
    setPhase('setup')
    setBubbles([])
    setError(null)
    callId.current = null
    elapsedRef.current = 0
  }, [])

  // ------------------------------------------------------------------ render --
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#111b21',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 420,
          maxWidth: '100%',
          height: 700,
          maxHeight: '92vh',
          background: WA.bg,
          borderRadius: 28,
          border: '1px solid rgba(255,255,255,.08)',
          boxShadow: shadow.modal,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Cabeçalho do contato */}
        <div
          style={{
            background: WA.header,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: WA.accent,
              color: '#04231c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 17,
            }}
          >
            {agentName.slice(0, 1)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: WA.text, fontWeight: 600, fontSize: 15 }}>
              {agentName} · Banco Vantagem
            </div>
            <div style={{ color: WA.faint, fontSize: 12 }}>
              {phase === 'live' ? (typing ? 'digitando…' : 'online') : 'WhatsApp · demonstração'}
            </div>
          </div>
          <span
            title={supervisors > 0 ? `supervisor acompanhando (${supervisors})` : 'sem supervisor'}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: supervisors > 0 ? WA.accent : 'rgba(255,255,255,.25)',
            }}
          />
          {phase === 'live' ? (
            <button
              type="button"
              onClick={() => hangup()}
              aria-label="Encerrar conversa"
              style={{
                border: 'none',
                background: 'transparent',
                color: WA.faint,
                fontSize: 18,
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          ) : null}
        </div>

        {/* Corpo */}
        {phase === 'setup' ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 16,
              padding: 24,
              color: WA.text,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11.5, color: WA.faint, letterSpacing: '.08em' }}>
                NEGOCIAÇÃO POR TEXTO
              </div>
              <div style={{ fontSize: 19, fontWeight: 600, marginTop: 8, lineHeight: 1.35 }}>
                Você responde como
                <br />
                {debtor?.name ?? '—'}
              </div>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <span style={{ fontSize: 11.5, color: WA.faint, fontWeight: 600 }}>
                Situação a simular
              </span>
              <select
                value={scenarioId}
                onChange={(event) => setScenarioId(event.target.value)}
                style={{
                  padding: '11px 12px',
                  borderRadius: radius.lg,
                  border: '1px solid rgba(255,255,255,.15)',
                  background: WA.input,
                  color: WA.text,
                  fontSize: 13,
                }}
              >
                {scenarios.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>

            {enabled === false ? (
              <div
                style={{
                  fontSize: 12,
                  color: '#ffd9cf',
                  background: 'rgba(237,79,46,.14)',
                  borderRadius: radius.lg,
                  padding: '10px 12px',
                  lineHeight: 1.5,
                }}
              >
                OPENAI_API_KEY não configurada — defina a chave no <code>.env</code> e reinicie o
                backend.
              </div>
            ) : null}

            {error ? (
              <div
                style={{
                  fontSize: 12,
                  color: '#ffd9cf',
                  background: 'rgba(237,79,46,.14)',
                  borderRadius: radius.lg,
                  padding: '10px 12px',
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            ) : null}

            <button
              type="button"
              onClick={() => void start()}
              disabled={!enabled || !debtor}
              style={{
                background: WA.accent,
                color: '#04231c',
                border: 'none',
                borderRadius: radius.pill,
                padding: '14px 24px',
                fontSize: 15,
                fontWeight: 700,
                cursor: enabled && debtor ? 'pointer' : 'not-allowed',
                opacity: enabled && debtor ? 1 : 0.5,
              }}
            >
              Iniciar conversa
            </button>

            {handoff && enabled ? (
              <button
                type="button"
                onClick={() => void attach()}
                style={{
                  background: 'transparent',
                  color: WA.accent,
                  border: `1px solid ${WA.accent}`,
                  borderRadius: radius.pill,
                  padding: '12px 24px',
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Continuar a ligação de{' '}
                {cases.find((c) => c.id === handoff.case_id)?.name ?? 'cliente'} aqui →
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div
              ref={scrollRef}
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {bubbles.map((bubble) => (
                <div
                  key={bubble.id}
                  style={{
                    alignSelf: bubble.from === 'cli' ? 'flex-end' : 'flex-start',
                    maxWidth: '82%',
                    background: bubble.from === 'cli' ? WA.sent : WA.received,
                    color: WA.text,
                    borderRadius: 10,
                    padding: '7px 10px',
                    fontSize: 13.5,
                    lineHeight: 1.45,
                    animation: 'bub .25s ease',
                  }}
                >
                  {bubble.artifact ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 12.5 }}>
                        {bubble.artifact.title}
                      </div>
                      {bubble.artifact.qr_svg ? (
                        <div
                          style={{ width: 150, background: '#fff', borderRadius: 8, padding: 8 }}
                          dangerouslySetInnerHTML={{ __html: bubble.artifact.qr_svg }}
                        />
                      ) : null}
                      <div style={{ fontSize: 11.5, color: WA.faint, lineHeight: 1.45 }}>
                        {bubble.artifact.meta}
                      </div>
                      {bubble.artifact.code ? (
                        <div
                          style={{
                            fontSize: 10.5,
                            fontFamily: 'monospace',
                            color: WA.faint,
                            wordBreak: 'break-all',
                            lineHeight: 1.4,
                          }}
                        >
                          {bubble.artifact.code}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    bubble.text
                  )}
                  <div
                    style={{
                      fontSize: 10,
                      color: bubble.from === 'cli' ? 'rgba(233,237,239,.6)' : WA.faint,
                      textAlign: 'right',
                      marginTop: 3,
                    }}
                  >
                    {bubble.at}
                  </div>
                </div>
              ))}

              {typing ? (
                <div
                  style={{
                    alignSelf: 'flex-start',
                    background: WA.received,
                    color: WA.faint,
                    borderRadius: 10,
                    padding: '9px 14px',
                    fontSize: 13,
                    animation: 'shim 1.2s ease infinite',
                  }}
                >
                  digitando…
                </div>
              ) : null}

              {error && phase === 'live' ? (
                <div
                  style={{
                    alignSelf: 'center',
                    fontSize: 11.5,
                    color: '#ffd9cf',
                    background: 'rgba(237,79,46,.14)',
                    borderRadius: radius.md,
                    padding: '6px 10px',
                  }}
                >
                  {error}
                </div>
              ) : null}

              {phase === 'ended' ? (
                <div
                  style={{
                    alignSelf: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 12,
                  }}
                >
                  <div style={{ fontSize: 12.5, color: WA.faint }}>Conversa encerrada</div>
                  <button
                    type="button"
                    onClick={restart}
                    style={{
                      background: WA.accent,
                      color: '#04231c',
                      border: 'none',
                      borderRadius: radius.pill,
                      padding: '10px 20px',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Nova conversa
                  </button>
                </div>
              ) : null}
            </div>

            {/* Entrada */}
            {phase === 'live' ? (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  padding: '10px 12px',
                  background: WA.header,
                  flex: 'none',
                }}
              >
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') send()
                  }}
                  placeholder={typing ? `${agentName} está digitando…` : 'Mensagem'}
                  // Nunca desabilitar: disabled derruba o foco e o apresentador
                  // teria de clicar de novo a cada resposta. Dá para digitar a
                  // próxima mensagem durante o turno da Alice — só o envio
                  // espera (o send() ignora com typing=true).
                  style={{
                    flex: 1,
                    border: 'none',
                    borderRadius: radius.pill,
                    background: WA.input,
                    color: WA.text,
                    padding: '11px 16px',
                    fontSize: 13.5,
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={send}
                  disabled={typing || !draft.trim()}
                  aria-label="Enviar"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    border: 'none',
                    background: WA.accent,
                    color: '#04231c',
                    fontSize: 17,
                    cursor: typing || !draft.trim() ? 'not-allowed' : 'pointer',
                    opacity: typing || !draft.trim() ? 0.5 : 1,
                  }}
                >
                  ➤
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
